import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Music } from "lucide-react";
import { instrumentOptions } from "@shared/schema";
import type { ModeConfig } from "./mode-config";

interface ParticipantFiltersProps {
  config: ModeConfig;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  playedFilter: string;
  onPlayedFilterChange: (value: string) => void;
  attendedFilter: "all" | "attended" | "not-attended";
  onAttendedFilterChange: (value: "all" | "attended" | "not-attended") => void;
  showPriorityOnly: boolean;
  onPriorityFilterChange: (value: boolean) => void;
  instrumentFilter: string[];
  onInstrumentFilterChange: (value: string[]) => void;
  typeFilter: string[];
  onTypeFilterChange: (value: string[]) => void;
  showFilters: boolean;
  onShowFiltersChange: (value: boolean) => void;
  onClearAllFilters?: () => void;
  hasActiveFilters?: boolean;
  forceMobileView?: boolean;
}

export function ParticipantFilters({
  config,
  searchTerm,
  onSearchChange,
  playedFilter,
  onPlayedFilterChange,
  attendedFilter,
  onAttendedFilterChange,
  showPriorityOnly,
  onPriorityFilterChange,
  instrumentFilter,
  onInstrumentFilterChange,
  typeFilter,
  onTypeFilterChange,
  showFilters,
  onShowFiltersChange,
  onClearAllFilters,
  hasActiveFilters: hasActiveFiltersProp,
  forceMobileView = false,
}: ParticipantFiltersProps) {
  const selectedInstrumentCount = instrumentFilter.length;
  const selectedTypeCount = typeFilter.length;
  const performerTypes = ["solo", "band"];

  const handleRoleFilterChange = (item: string, checked: boolean) => {
    if (config.hasInstrumentFilter) {
      if (checked) {
        onInstrumentFilterChange([...instrumentFilter, item]);
      } else {
        onInstrumentFilterChange(instrumentFilter.filter((i) => i !== item));
      }
    } else if (config.hasTypeFilter) {
      if (checked) {
        onTypeFilterChange([...typeFilter, item]);
      } else {
        onTypeFilterChange(typeFilter.filter((i) => i !== item));
      }
    }
    // Don't close the popover when selecting/deselecting items
  };

  const clearRoleFilters = () => {
    if (config.hasInstrumentFilter) {
      onInstrumentFilterChange([]);
    } else if (config.hasTypeFilter) {
      onTypeFilterChange([]);
    }
  };

  const hasRoleFilters = config.hasInstrumentFilter || config.hasTypeFilter;
  const roleItems = config.hasInstrumentFilter ? instrumentOptions : performerTypes;
  const roleFilterCount = config.hasInstrumentFilter ? selectedInstrumentCount : selectedTypeCount;
  const hasActiveRoleFilters = config.hasInstrumentFilter ? instrumentFilter.length > 0 : typeFilter.length > 0;

  // Check if any filters are active
  const hasActiveFilters = hasActiveFiltersProp !== undefined ? hasActiveFiltersProp : (
    searchTerm !== "" || 
    playedFilter !== "all" || 
    attendedFilter !== "all" || 
    showPriorityOnly || 
    instrumentFilter.length > 0 || 
    typeFilter.length > 0
  );

  const clearAllFilters = onClearAllFilters || (() => {
    onSearchChange("");
    onPlayedFilterChange("all");
    onAttendedFilterChange("all");
    onPriorityFilterChange(false);
    onInstrumentFilterChange([]);
    onTypeFilterChange([]);
  });

  // Determine if we should show mobile layout
  const showMobileLayout = forceMobileView || false;

  return (
    <div className="px-6 py-4 bg-white/10 border-b border-white/20 relative z-10">
      {/* Mobile layout: stacked */}
      <div className={showMobileLayout ? "flex flex-col gap-3" : "flex flex-col gap-3 sm:hidden"}>
        {/* Search bar on its own row */}
        <div className="relative w-full">
          <Input
            placeholder="Search participants..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-8 w-full"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
              onClick={() => onSearchChange("")}
            >
              <X className="h-3 w-3 text-slate-400 hover:text-slate-600" />
            </Button>
          )}
        </div>

        {/* Other filters on second row */}
        <div className={`grid gap-2 ${hasRoleFilters ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {/* Status Filter */}
          <Select 
            value={config.isGenericEvent ? attendedFilter : playedFilter} 
            onValueChange={config.isGenericEvent ? onAttendedFilterChange : onPlayedFilterChange}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {config.isGenericEvent ? (
                <>
                  <SelectItem value="attended">Attended</SelectItem>
                  <SelectItem value="not-attended">Not Attended</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="played">Played</SelectItem>
                  <SelectItem value="not-played">Not Played</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>

          {/* Role/Instrument Filter */}
          {hasRoleFilters && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="relative w-full"
                >
                  <Music className="h-4 w-4 mr-1" />
                  <span>Role</span>
                  {roleFilterCount > 0 && (
                    <Badge className="ml-1 h-4 px-1 rounded-full flex items-center justify-center text-[10px]">
                      {roleFilterCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="start">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-muted-foreground">
                    Filter by {config.hasInstrumentFilter ? "Instruments" : "Type"}
                  </p>
                </div>
                <DropdownMenuSeparator />
                {roleItems.map((item) => (
                  <DropdownMenuCheckboxItem
                    key={`mobile-${item}`}
                    checked={config.hasInstrumentFilter 
                      ? instrumentFilter.includes(item)
                      : typeFilter.includes(item)
                    }
                    onCheckedChange={(checked) => handleRoleFilterChange(item, checked)}
                    className="capitalize"
                  >
                    {item}
                  </DropdownMenuCheckboxItem>
                ))}
                {hasActiveRoleFilters && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearRoleFilters}
                        className="w-full"
                      >
                        Clear all
                      </Button>
                    </div>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Priority Filter */}
          <Button
            variant={showPriorityOnly ? "default" : "outline"}
            className="w-full"
            onClick={() => onPriorityFilterChange(!showPriorityOnly)}
          >
            Priority {showPriorityOnly && "✓"}
          </Button>
        </div>
      </div>

      {/* Desktop layout: horizontal */}
      <div className={showMobileLayout ? "hidden" : "hidden sm:flex sm:flex-row gap-4"}>
        <div className="flex-1">
          <div className="relative max-w-xs">
            <Input
              placeholder="Search participants..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pr-8"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={() => onSearchChange("")}
              >
                <X className="h-3 w-3 text-slate-400 hover:text-slate-600" />
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          {/* Status Filter */}
          <Select 
            value={config.isGenericEvent ? attendedFilter : playedFilter} 
            onValueChange={config.isGenericEvent ? onAttendedFilterChange : onPlayedFilterChange}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {config.isGenericEvent ? (
                <>
                  <SelectItem value="attended">Attended</SelectItem>
                  <SelectItem value="not-attended">Not Attended</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="played">Played</SelectItem>
                  <SelectItem value="not-played">Not Played</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>

          {/* Role/Instrument Filter */}
          {hasRoleFilters && (
            <Popover open={showFilters} onOpenChange={onShowFiltersChange}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="relative">
                  <Music className="h-4 w-4 mr-2" />
                  Role
                  {roleFilterCount > 0 && (
                    <Badge className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">
                      {roleFilterCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56" align="end">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm">
                    Filter by {config.hasInstrumentFilter ? "Instruments" : "Type"}
                  </h4>
                  {roleItems.map((item) => (
                    <div
                      key={`desktop-${item}`}
                      className="flex items-center space-x-2"
                    >
                      <Checkbox
                        id={`desktop-${item}`}
                        checked={config.hasInstrumentFilter 
                          ? instrumentFilter.includes(item)
                          : typeFilter.includes(item)
                        }
                        onCheckedChange={(checked) => handleRoleFilterChange(item, !!checked)}
                      />
                      <label
                        htmlFor={`desktop-${item}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
                      >
                        {item}
                      </label>
                    </div>
                  ))}
                  {hasActiveRoleFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearRoleFilters}
                      className="w-full"
                    >
                      Clear all
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* Priority Filter */}
          <div className="flex items-center space-x-2 px-3 py-2 border rounded-md h-10">
            <Switch
              id="priority-filter-desktop"
              checked={showPriorityOnly}
              onCheckedChange={onPriorityFilterChange}
            />
            <label
              htmlFor="priority-filter-desktop"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Priority
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}